/**
 * 1010 建站舱流程改造 · 站点流程 API(生成 / 预览调整 / 上线)。
 * 契约:docs/2026-10-10-建站舱-模板原样/契约-建站舱流程改造.md(api 线 v1)。
 *
 * ★ 与 websiteService 同一条纪律:写口(扣费 / 发布 / 建栏目 / 建接入点)一律不兜底,直接抛;
 *   这里**没有 dev 桩** —— 生成流程全是写口与进度轮询,假装成功比报错更糟。
 */
import { api } from "@/shared/api";

import type {
  FlowImagePanel,
  FlowImageSlot,
  FlowJob,
  FlowSuggestion,
  GenerateRequest,
  ImageQuote,
  ImageQuoteParams,
  LaunchChat,
  LaunchNewsRequest,
  LaunchView,
  NewsStatus,
  RegenerateImageRequest,
  RepublishResult,
  SetFlowImageRequest,
  SourcesView,
  UnlinkChatResult,
} from "./flowTypes";
import type { UploadImageResult } from "./types";

export * from "./flowTypes";

const site = (id: string) => `/website/sites/${encodeURIComponent(id)}`;

/** 积分不足(契约:402 INSUFFICIENT_CREDITS)。鸭子判型,原因同 websiteService.configIncompleteDetails。 */
export function isInsufficientCredits(e: unknown): boolean {
  const err = e as { errorCode?: unknown; httpStatus?: unknown } | null | undefined;
  return err?.errorCode === "INSUFFICIENT_CREDITS" || err?.httpStatus === 402;
}

export const flowService = {
  // ── ① 生成 ──
  sources: (id: string) => api.get<SourcesView>(`${site(id)}/flow/sources`),
  quote: (id: string, p: ImageQuoteParams) => {
    const q = new URLSearchParams({
      aiImages: String(p.aiImages),
      galleryCount: String(p.galleryCount),
    });
    if (p.templateCode) q.set("templateCode", p.templateCode);
    return api.get<ImageQuote>(`${site(id)}/flow/quote?${q.toString()}`);
  },
  /** 客户自有图转存(purpose=gallery,base64 不带 dataURL 前缀)。 */
  uploadGallery: (id: string, data: string) =>
    api.post<UploadImageResult>(`${site(id)}/assets/image`, { data, purpose: "gallery" }),
  /** 写口:full 扣费;402 INSUFFICIENT_CREDITS;409 同站进行中 / 已发布过的站换模板。 */
  generate: (id: string, body: GenerateRequest) =>
    api.post<FlowJob>(`${site(id)}/flow/generate`, body),
  job: (id: string, jobId: string) =>
    api.get<FlowJob>(`${site(id)}/flow/jobs/${encodeURIComponent(jobId)}`),
  latestJob: (id: string) => api.get<FlowJob | null>(`${site(id)}/flow/jobs/latest`),

  // ── ② 预览调整 ──
  suggestions: (id: string) => api.get<FlowSuggestion[]>(`${site(id)}/flow/suggestions`),
  images: (id: string) => api.get<FlowImagePanel>(`${site(id)}/flow/images`),
  /** 只改草稿,随后调 republish。 */
  setImage: (id: string, body: SetFlowImageRequest) =>
    api.put<FlowImageSlot>(`${site(id)}/flow/images`, body),
  republish: (id: string) => api.post<RepublishResult>(`${site(id)}/flow/republish`, undefined),
  /** 单张 AI 重画(异步,成功扣费、失败不扣,结束自动重发预览)。 */
  regenerateImage: (id: string, body: RegenerateImageRequest) =>
    api.post<FlowJob>(`${site(id)}/flow/images/regenerate`, body),

  // ── ③ 上线 ──
  launch: (id: string) => api.get<LaunchView>(`${site(id)}/flow/launch`),
  setNews: (id: string, body: LaunchNewsRequest) =>
    api.post<NewsStatus>(`${site(id)}/flow/launch/news`, body),
  linkChat: (id: string) => api.post<LaunchChat>(`${site(id)}/flow/launch/chat`, {}),
  /** 取消接入:撤销站点按钮链接(接口名待 api 线对齐,先按 DELETE)。写口。 */
  unlinkChat: (id: string) => api.delete<UnlinkChatResult>(`${site(id)}/flow/launch/chat`),
};

export type FlowService = typeof flowService;
